import { CoreError } from '@goodt-wcore/errors';
import { LifetimeType, ContainerValueType } from './constants';

/**
 * @typedef {object} ServiceRecordConfig
 * @property {symbol} type
 * @property {symbol} lifetime
 */
/**
 * @typedef {object} ContainerRecord
 * @property {any} value
 * @property {ServiceRecordConfig} config
 */

export class ServiceContainer {
    /**
     * @type {Map<symbol, ContainerRecord>}
     */
    _registry = new Map();

    /**
     * @type {WeakMap<ContainerRecord|any, any>}
     */
    _artifactory = new WeakMap();

    /**
     *
     * @return {ServiceContainer}
     */
    static create() {
        return new ServiceContainer();
    }

    /**
     * @param {symbol} serviceId
     * @param {Record<string, any>} [options={}]
     * @param {boolean} useMerge
     * @return {ServiceContainer}
     */
    setOptions(serviceId, options = {}, useMerge = false) {
        if (this._registry.has(serviceId) === false) {
            throw new CoreError(`Service "${serviceId.toString()}" does not exist in container to apply "setOptions".`);
        }

        const record = this._registry.get(serviceId);
        const artifacts = this._artifactory.get(record);
        // prettier-ignore
        const { config: { lifetime } } = record;

        if (lifetime === LifetimeType.SINGLETON && artifacts.instance != null) {
            throw new CoreError(
                `Unable to apply "setOptions" to "${serviceId.toString()}" due to lifetime type is "${LifetimeType.SINGLETON.toString()} and instance exists"`
            );
        }
        artifacts.options = useMerge ? { ...artifacts?.options, ...options } : options;

        return this;
    }

    /**
     * @param {symbol} contentId
     * @param {any} value
     * @param {ServiceRecordConfig} config
     *
     * @return {ServiceContainer}
     */
    register(contentId, value, config) {
        // prettier-ignore
        const record = { value, config };
        this._registry.set(contentId, record);
        this._artifactory.set(record, {});

        return this;
    }

    /**
     * @param {symbol} contentId
     * @return {ServiceContainer}
     */
    remove(contentId) {
        this._registry.delete(contentId);
        return this;
    }

    /**
     * @param {symbol} serviceId
     * @param {Record<any, string>} [customOptions={}]
     * @return {any|null}
     */
    get(serviceId, customOptions) {
        if (this.has(serviceId) === false) {
            return null;
        }

        const record = this._registry.get(serviceId);
        // prettier-ignore
        const { value, config: { type, lifetime } } = record;

        const artifacts = this._artifactory.get(record);
        const { options } = artifacts;

        if (value == null && [LifetimeType.TRANSIENT, LifetimeType.SINGLETON].includes(lifetime)) {
            throw new CoreError(
                `Failed to get "${serviceId.toString()}" from "ServiceContainer": ` +
                    `unable to create instance for "${serviceId.toString()}" cause "instantiator" is not callable.`,
                { reason: record }
            );
        }

        if (lifetime === LifetimeType.TRANSIENT) {
            return this._createInstance(value, { ...options, ...customOptions }, type);
        }

        if (lifetime === LifetimeType.SINGLETON) {
            artifacts.instance ??= this._createInstance(value, options, type);
            return artifacts.instance;
        }

        return value;
    }

    has(serviceId) {
        return this._registry.has(serviceId);
    }

    /**
     * @template {O}
     * @param {(function(options: O): any)} instantiator
     * @param {{ setOptions?: (function(options: O): any) }} instantiator
     * @param {O} options
     * @param {symbol} type
     */
    _createInstance(instantiator, options, type) {
        if (type === ContainerValueType.FACTORY) {
            return instantiator(options);
        }
        if (type === ContainerValueType.CONSTRUCTOR) {
            const Ctor = instantiator;
            return new Ctor(options);
        }
        if (typeof instantiator.setOptions === 'function') {
            instantiator.setOptions(options);
        }
        return instantiator;
    }
}
